/**
 * Pure customer-suggestion engine for the TT order metadata form. Extracted
 * (unchanged behavior) from the original AttachCustomerForm so it can be
 * unit tested and reused by both v1 and v2 forms.
 *
 * The algorithm scores each text field independently against each customer
 * name and picks the customer with the best per-field score (this prevents
 * noisy fields dragging down a clean match in another field).
 *
 * Order of resolution (first hit wins):
 *  1. Whitespace-collapsed substring match (with a small legal-suffix strip).
 *     If exactly one customer matches OR the longest match is strictly longer
 *     than the runner-up, return it.
 *  2. Levenshtein-based segment scoring above a confidence threshold.
 *  3. Abbreviation match ("walter m" / "walterm" → "Walter Matthew") — only
 *     fires when exactly one customer matches (multiple matches = ambiguous).
 */

export interface OrderTextFields {
  textA?: string | null;
  textB?: string | null;
  textC?: string | null;
  textTT?: string | null;
}

export interface SuggestCustomerInput {
  id: string;
  longName: string;
}

const SUGGESTION_CONFIDENCE_THRESHOLD = 0.65;

/** Tokens that are too generic to be meaningful for matching. */
const NOISE_TOKENS = new Set([
  "farm", "farms", "new", "hta", "spot", "otc", "pricing", "pxing", "pos", "adj", "llc", "ltd", "group",
  "inc", "co", "corp", "the", "and", "of", "grain", "grains", "ag", "agr", "or", "&", "error",
]);

/** Words stripped from both sides before collapsed matching. */
const COLLAPSED_STRIP_WORDS = /\b(farm|llc|ltd|inc|corp|co|new|hta|otc|error|group)\b/g;

/** Normalise a raw string into matchable segments (≥ 3 chars, not noise). */
function toSegments(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3 && !NOISE_TOKENS.has(t));
}

/** Levenshtein edit distance between two strings. */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr: number[] = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost
      );
    }
    prev = curr;
  }
  return prev[n];
}

/** Score how well a single input segment matches a single customer name segment. */
function segmentScore(input: string, nameSegment: string): number {
  if (input === nameSegment) return 1.0;
  const shorter = input.length < nameSegment.length ? input : nameSegment;
  const longer = input.length < nameSegment.length ? nameSegment : input;
  if (longer === shorter + "s") return 0.95;
  const maxLen = Math.max(input.length, nameSegment.length);
  if (maxLen === 0) return 1.0;
  const dist = levenshtein(input, nameSegment);
  return 1 - dist / maxLen;
}

function bestSegmentScore(inputSeg: string, nameSegments: string[]): number {
  if (nameSegments.length === 0) return 0;
  return nameSegments.reduce((best, ns) => Math.max(best, segmentScore(inputSeg, ns)), 0);
}

function customerConfidenceScore(inputSegments: string[], nameSegments: string[]): number {
  if (nameSegments.length === 0 || inputSegments.length === 0) return 0;
  const nameScores = nameSegments.map((ns) => bestSegmentScore(ns, inputSegments));
  const avg = nameScores.reduce((sum, s) => sum + s, 0) / nameScores.length;
  return avg;
}

function fieldMatchesAbbreviation(fieldRaw: string, customerNameRaw: string): boolean {
  const nameParts = customerNameRaw
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (nameParts.length < 2) return false;

  const haystack = fieldRaw.toLowerCase();

  for (let i = 0; i < nameParts.length; i++) {
    for (let j = 0; j < nameParts.length; j++) {
      if (i === j) continue;
      const full = nameParts[i];
      const initial = nameParts[j][0];
      if (!initial) continue;
      if (haystack.includes(`${full} ${initial}`) || haystack.includes(`${full}${initial}`)) {
        return true;
      }
    }
  }
  return false;
}

function anyFieldMatchesAbbreviation(
  fields: Array<string | null | undefined>,
  customerLongName: string
): boolean {
  return fields.some((f) => f && fieldMatchesAbbreviation(f, customerLongName));
}

function normaliseCollapsed(text: string): string {
  return text
    .toLowerCase()
    .replace(COLLAPSED_STRIP_WORDS, "")
    .replace(/[^a-z0-9]/g, "");
}

function fieldMatchesCollapsed(fieldRaw: string, customerNameRaw: string): boolean {
  const fieldCollapsed = normaliseCollapsed(fieldRaw);
  const nameCollapsed = normaliseCollapsed(customerNameRaw);
  if (nameCollapsed.length === 0) return false;
  if (fieldCollapsed.length === 0) return false;
  return nameCollapsed.startsWith(fieldCollapsed) || nameCollapsed.endsWith(fieldCollapsed);
}

function anyFieldMatchesCollapsed(
  fields: Array<string | null | undefined>,
  customerLongName: string
): boolean {
  return fields.some((f) => f && fieldMatchesCollapsed(f, customerLongName));
}

/**
 * Returns the best-matching customer id for the given order text fields, or
 * undefined if no confident match was found.
 */
export function getSuggestedCustomerId({
  orderTextFields,
  customers,
}: {
  orderTextFields: OrderTextFields | null | undefined;
  customers: SuggestCustomerInput[];
}): string | undefined {
  if (!orderTextFields || customers.length === 0) return undefined;

  const { textA, textB, textC, textTT } = orderTextFields;
  const rawFields = [textA, textB, textC, textTT];

  const fieldSegments = rawFields
    .map((t) => (t ? toSegments(t) : []))
    .filter((segs) => segs.length > 0);
  if (fieldSegments.length === 0) return undefined;

  // 1. Collapsed-whitespace match (longest unambiguous wins).
  const collapsedMatches = customers
    .filter((customer) => anyFieldMatchesCollapsed(rawFields, customer.longName))
    .sort((a, b) => {
      const lenDiff = normaliseCollapsed(b.longName).length - normaliseCollapsed(a.longName).length;
      if (lenDiff !== 0) return lenDiff;
      return a.id.localeCompare(b.id);
    });

  if (
    collapsedMatches.length >= 1 &&
    (
      collapsedMatches.length === 1 ||
      normaliseCollapsed(collapsedMatches[0].longName).length >
      normaliseCollapsed(collapsedMatches[1].longName).length
    )
  ) {
    return collapsedMatches[0].id;
  }

  // 2. Fuzzy segment scoring above threshold.
  const scored = customers
    .map((customer) => {
      const nameSegments = toSegments(customer.longName);
      if (nameSegments.length === 0) return { customer, score: 0 };
      const score = fieldSegments.reduce(
        (best, segs) => Math.max(best, customerConfidenceScore(segs, nameSegments)),
        0
      );
      return { customer, score };
    })
    .filter((entry) => entry.score >= SUGGESTION_CONFIDENCE_THRESHOLD)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.customer.id.localeCompare(b.customer.id);
    });

  if (scored.length > 0) {
    return scored[0].customer.id;
  }

  // 3. Abbreviation fallback (single-match only).
  const abbreviationMatches = customers
    .filter((customer) => anyFieldMatchesAbbreviation(rawFields, customer.longName))
    .sort((a, b) => a.id.localeCompare(b.id));

  if (abbreviationMatches.length === 1) {
    return abbreviationMatches[0].id;
  }

  return undefined;
}
